<template>
  <div class="w-full group product-card">
    <a :href="productUrl" class="block w-full pb-[133%] relative bg-lightest-grey" :aria-label="product.title">

      <div v-if="(product.media.length > 1)" v-fade class="hidden laptop:block absolute w-full h-full fade-in opacity-0 group-hover:opacity-0 transition-opacity duration-300">
        <img 
          loading="eager"
          class="absolute object-contain w-full h-full"
          :class="!product.available ? 'opacity-20 group-hover:opacity-0': '' "
          :src="`${product.media[0].src}&width=320`"
          :alt="`${product.vendor} ${product.title}`"
          :srcset="`${productImages[0].srcset}`"
          sizes="(max-width: 1024px) 50vw, 25vw"
        >
      </div>
    
      <div v-else v-fade class="hidden laptop:block absolute w-full h-full opacity-100">
        <img 
          loading="lazy"
          class="absolute object-contain w-full h-full"
          :class="!product.available ? 'opacity-20': '' "
          :src="`${product.media[0].src}&width=320`"
          :alt="`${product.vendor} ${product.title}`"
          :srcset="`${productImages[0].srcset}`"
          sizes="(max-width: 1024px) 50vw, 25vw"
        >
      </div>
        
      <div :id="'carousel-' + carouselIndex" v-if="(product.media.length > 0)" class="flex laptop:hidden absolute w-full h-full touch-pan-x overflow-y-hidden overflow-x-auto snap-x snap-mandatory image-carousel hide-scrollbar">
        <div :data-index="i" v-for="(image, i) in productImages" :key="i" class="snap-always snap-start basis-full h-full shrink-0 relative bg-lightest-grey overflow-x-auto overflow-y-hidden" :class="!product.available ? 'opacity-20': '' ">
          <img 
            loading="lazy"
            class="absolute object-contain w-full h-full"
            :src="`${image.src}&width=320`"
            :alt="`${product.vendor} ${product.title}`"
            :srcset="`${image.srcset}`"
            sizes="(max-width: 1024px) 50vw, 25vw"
          >
        </div>
      </div>
    
      <div v-else class="laptop:hidden absolute w-full h-full" :class="!product.available ? 'opacity-20': '' ">
          <img src="{{ 'static.gp-no-image.png' | asset_url }}" alt="No Image" class=" absolute w-full h-full object-contain">
      </div>
        
      <div v-if="(product.media.length > 1)" class="hidden laptop:flex opacity-0 group-hover:opacity-100 transition-opacity duration-300 absolute w-full h-full touch-pan-x overflow-x-auto overflow-y-hidden snap-x snap-mandatory horizontal-scrollbars" tabindex="0">
        <div v-for="(image, i) in productImages.slice(1)" :key="i" class="snap-always snap-start basis-full h-full shrink-0 relative bg-lightest-grey overflow-x-auto overflow-y-hidden" :class="!product.available ? 'opacity-20': '' ">
          <img 
            loading="lazy"
            class="absolute object-contain w-full h-full"
            :src="`${image.src}&width=320`"
            :alt="`${product.vendor} ${product.title}`"
            :srcset="`${image.srcset}`"
            sizes="(max-width: 1024px) 50vw, 25vw"
          >
        </div>
      </div>

      <div :id="'pagination-' + carouselIndex" class="pagination w-full h-1 bg-grey absolute bottom-0 flex">
        <span v-for="(image, i) in productImages" :key="i" class="pagination-item flex-1" :data-index="i"></span>
      </div>
      
      <div class="absolute w-full h-full top-0 left-0 p-12 body-2 pointer-events-none">
        <div v-if="newProduct" class="text-dark-grey">
          New
        </div>
        <div class="text-red" v-if="onSale">
          -{{ salePercent }}%
        </div>
        <div v-if="!product.available" class="text-dark-grey">
          Sold out
        </div>
      </div>
    </a>

    <div class="text-sm leading-xs px-8 py-12 relative">
      <div class="mb-4">
        {{ product.vendor }}
      </div>
      <div class="mb-4">
        <a :href="productUrl" class="no-underline">{{ product.title }}</a>
      </div>
      <div>
        <template v-if="onSale">
          <s class="text-medium-grey">{{ formatPrice(product.compare_at_price_min) }}</s>
          <span class="text-red ml-4">{{ formatPrice(product.price_min) }}</span>
        </template>
        <template v-else>
          <span v-if="product.price_varies">From </span>
          <span>{{ formatPrice(product.price_min) }}</span>
        </template>
      </div>
      <div v-if="product.available && showAddToCart" class=" laptop:hidden block mt-4">
        <button @click.prevent="handleAddToCart" :disabled="loading" class="underline underline-offset-2 flex items-center gap-4 min-h-20 disabled:text-medium-grey">Add to bag <vital-spinner v-if="loading"></vital-spinner></button>
        <Transition name="blink">
          <div v-if="error" class="mt-8 body-2 text-red">{{ error }}</div>
        </Transition>
      </div>
     
      <div v-if="(product.variants.length > 1)" class="hidden laptop:block opacity-0 group-hover:opacity-100 transition-opacity absolute top-0 left-0 w-full h-full px-8 py-12 bg-white overflow-hidden">
        <div v-if="(product.variants.length < 10)">
            Size
            <span class="transition-opacity" aria-hidden="true" data-avail-notice>{{ noticeMessage }}</span> 
            <div class="mt-4 line-clamp-2">
              <a  
                v-for="(variant, i) in vitalProduct.variants"
                @mouseover="showNotice($event)"
                @mouseleave="hideNotice"
                :key="i"
                :href="productUrl"
                :data-avail="variant.remaining"
                class="inline-block mr-8 mb-4"
                :class="variant.available ? 'no-underline hover:underline hover:text-dark-grey cursor-pointer' : 'line-through text-medium-grey'"
              >
                {{ variant.option2 }}
              </a>
            </div>
            <div v-if="product.available && showAddToCart" class="mb-8">
              <button @click.prevent="handleAddToCart" :disabled="loading" class="underline underline-offset-2 flex items-center gap-4 min-h-20 disabled:text-medium-grey">Add to bag <vital-spinner v-if="loading"></vital-spinner></button>
              <Transition name="blink">
                <div v-if="error" class="mt-8 body-2 text-red">{{ error }}</div>
              </Transition>
            </div>
          </div>
        <div v-else>
          <a
              :href="productUrl"
              class="inline-block mr-8 mb-4 no-underline hover:underline hover:text-dark-grey"
          >
          Click to see available sizes
          </a>
        </div>
      </div>
       
    </div>
  </div>
</template>

<script>
import {useVitalUtilities} from "@/vital";
import NoPhoto from '../../images/no-photo.png'
import {useCartStore} from "@/vue/store/cart";
import {ref, computed} from "vue";
import VitalSpinner from "@/vue/components/vital-spinner";

export default {
  props: {
    collection: {
      default: null
    },
    product: {
      default: null
    },
    addToCart: {
      default: false
    },
    inventory: {
      default: null
    },
    carouselIndex: {
      default: null
    }
  },
  components: {
    VitalSpinner
  },
  setup(props) {
    const {formatPrice} = useVitalUtilities()
    const cartStore = useCartStore()

    const carouselIndex = ref(props.carouselIndex)

    const showAddToCart = ref(props.addToCart)
    const error = ref(null)
    const loading = ref(false)
    // add the product availability to the product array
    const vitalProduct = computed(() => {
      if (props.inventory) {

        const mergeArrays = () => {
          const inventory = props.inventory
          const product = ref(props.product)
          
          for (const item of inventory) {
            const existingItem = product.value.variants.find((variant) => variant.id === item.id);
            if (existingItem) {
              // If the variant already exists, update its properties
              Object.assign(existingItem, item);
            } else {
              // If the variant doesn't exist, push it to the variants array
              product.value[0].variants.push(item);
            }
          }
          return product.value;
        }
        return(mergeArrays())
       
      } else {
        return props.product
      }
    })

    const noticeMessage = ref('');

    const showNotice = (event) => {
      const target = event.target;
      const availability = target.getAttribute('data-avail');

      if (availability === '0') {
        noticeMessage.value = '— Sold Out';
      } else if (availability === '1') {
        noticeMessage.value = '— One in Stock';
      } else {
        noticeMessage.value = '';
      }
    };

    const hideNotice = () => {
      noticeMessage.value = '';
    };

    // create a srcset object in the product image array
    const createSrcsetFromImages = (mixedArray) => {
      const sizes = [180, 360, 540, 720, 900, 1080, 1296, 1512, 1728, 2048];
      
      const srcset = mixedArray
        .filter(obj => obj.media_type !== 'video') // Filter out video assets
        .map(obj => {
          const imageSrc = obj.src;
          const imageSrcset = imageSrc
            ? sizes.map(size => `${imageSrc}&width=${size} ${size}w`).join(', ')
            : '';

          return {
            ...obj,
            srcset: imageSrcset
          };
        });
      return srcset;
    }
  
    let productImages = createSrcsetFromImages(props.product.media)

    const productUrl = props.product?.url
        ? props.product.url
        : ((props.collection)
              ? `${window.Shopify.routes.root}collections/${props.collection}/products/${props.product.handle}`
              : `${window.Shopify.routes.root}collections/${props.product.vendor}/products/${props.product.handle}`
          )
          
    const now = new Date().getTime()
    const created = Date.parse(props.product.created_at)
    const diff = (now-created) / 1000 / 86400 // difference between now and created date in days
    const newProduct = diff <= 14 // new products are newer than 14 days

    const onSale = props.product.compare_at_price_min > props.product.price_min
    const salePercent = Math.floor((props.product.compare_at_price_min - props.product.price_min) * 100 / props.product.compare_at_price_max);

    const handleAddToCart = async () => {
      const firstAvailableVariant = props.product.variants.find(v => v.available === true)
      if (!firstAvailableVariant) {
        error.value = "Product is not available"
      }
      loading.value = true
      try {
        await cartStore.addItem(firstAvailableVariant.id, 1)
        await cartStore.updateCart()
        error.value = null
      } catch (e) {
        error.value = e.message
      }
      loading.value = false
    }

    return {
      formatPrice,
      productUrl,
      productImages,
      newProduct,
      onSale,
      salePercent,
      NoPhoto,
      handleAddToCart,
      showAddToCart,
      error,
      vitalProduct,
      loading,
      showNotice,
      hideNotice,
      noticeMessage
    }
  },
}
</script>